import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Check, ChevronRight, Users, Globe } from 'lucide-react';
import { TOOLS } from './NosOutilsPage';
import { SiteHeader, SiteFooter } from '../../components/website/SharedLayout';

const ToolDetailPage = () => {
  const { slug } = useParams();
  const tool = TOOLS.find(t => t.slug === slug);
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!tool) return <Navigate to="/site/nos-outils" replace />;

  const accentMap = {
    violet: {
      bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200',
      ring: 'ring-violet-100', badge: 'bg-violet-100 text-violet-700',
      stepBg: 'bg-violet-600', stepText: 'text-violet-600',
      btnGrad: 'from-violet-600 to-purple-700', shadow: 'shadow-violet-500/30'
    },
    emerald: {
      bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200',
      ring: 'ring-emerald-100', badge: 'bg-emerald-100 text-emerald-700',
      stepBg: 'bg-emerald-600', stepText: 'text-emerald-600',
      btnGrad: 'from-emerald-600 to-teal-700', shadow: 'shadow-emerald-500/30'
    }
  };
  const a = accentMap[tool.accentColor] || accentMap.violet;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader transparent={true} />
      {/* Hero */}
      <section className={`relative bg-gradient-to-br ${tool.color} pt-32 pb-24 overflow-hidden`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
        </div>
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <Link
            to="/site/nos-outils"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8 text-sm"
            data-testid="tool-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Tous nos outils
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="tool-detail-title">
                {tool.name}
              </h1>
              <p className="text-lg sm:text-xl text-white/80 mb-6 leading-relaxed">
                {tool.longDescription}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-semibold rounded-full hover:bg-white/90 transition-all"
                  data-testid="tool-visit-btn"
                >
                  Visiter {tool.name}
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/site#contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-all"
                >
                  Nous contacter
                </Link>
              </div>
            </div>
            {/* App Preview */}
            <div className="hidden lg:block">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-3 shadow-2xl">
                <div className="rounded-xl overflow-hidden bg-white aspect-video relative">
                  {!iframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                      <div className="text-center">
                        <Globe className="w-10 h-10 text-slate-300 mx-auto mb-2 animate-pulse" />
                        <span className="text-sm text-slate-400">Chargement de l'aperçu...</span>
                      </div>
                    </div>
                  )}
                  <iframe
                    ref={iframeRef}
                    src={tool.url}
                    title={`Aperçu de ${tool.name}`}
                    className="w-full h-full pointer-events-none"
                    style={{ minHeight: '320px' }}
                    onLoad={() => setIframeLoaded(true)}
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2 px-2">
                  <div className="flex gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/50" />
                  </div>
                  <div className="flex-1 bg-white/10 rounded-full px-3 py-1 flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-white/50" />
                    <span className="text-xs text-white/50">{tool.url}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats/Benefits */}
      <section className="max-w-6xl mx-auto px-4 -mt-10 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {tool.benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg p-5 text-center border border-slate-100">
              <div className={`text-2xl font-black ${a.text} mb-1`}>{b.stat}</div>
              <div className="text-sm font-semibold text-slate-700">{b.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Target Audience */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${a.badge} text-xs font-semibold rounded-full mb-4`}>
              <Users className="w-3.5 h-3.5" />
              Public cible
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              À qui s'adresse {tool.name} ?
            </h2>
            <p className="text-slate-600 mb-6">
              {tool.name} a été conçu pour répondre aux besoins spécifiques de professionnels qui cherchent une solution simple, efficace et prête à l'emploi.
            </p>
            <ul className="space-y-3">
              {tool.targetAudience.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full ${a.stepBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Mobile Preview */}
          <div className="flex justify-center">
            <div className="relative w-[280px]">
              <div className="bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl">
                <div className="bg-slate-900 rounded-[2rem] overflow-hidden">
                  <div className="bg-slate-900 h-6 flex items-center justify-center">
                    <div className="w-20 h-4 bg-slate-800 rounded-full" />
                  </div>
                  <div className="rounded-b-[1.8rem] overflow-hidden bg-white" style={{ height: '480px' }}>
                    <iframe
                      src={tool.url}
                      title={`${tool.name} mobile`}
                      className="w-[375px] h-[667px] pointer-events-none origin-top-left"
                      style={{ transform: 'scale(0.747)' }}
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={`${a.bg} py-20`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Fonctionnalités principales
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Tout ce dont vous avez besoin, dans une interface simple et intuitive.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tool.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow" data-testid={`tool-feature-${i}`}>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Comment ça fonctionne ?
          </h2>
          <p className="text-slate-600">En 4 étapes simples, vous êtes opérationnel.</p>
        </div>
        <div className="grid md:grid-cols-4 gap-6">
          {tool.howItWorks.map((s, i) => (
            <div key={i} className="relative text-center">
              {i < tool.howItWorks.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-[80%]">
                  <ChevronRight className="w-5 h-5 text-slate-200 mx-auto" />
                </div>
              )}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center mx-auto mb-4 shadow-lg ${a.shadow}`}>
                <span className="text-xl font-bold text-white">{s.step}</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`bg-gradient-to-br ${tool.color} py-16`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Prêt à découvrir {tool.name} ?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Testez la plateforme gratuitement ou contactez-nous pour une démonstration personnalisée.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-slate-900 font-semibold rounded-full hover:bg-white/90 transition-all shadow-lg"
              data-testid="tool-cta-visit"
            >
              Accéder à {tool.name}
              <ExternalLink className="w-4 h-4" />
            </a>
            <Link
              to="/site#contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-white/20 transition-all"
            >
              Demander une démo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default ToolDetailPage;
